import { useState, type FormEvent } from 'react';
import { type MutationStatus } from '@tanstack/react-query';
import {
  Grid,
  Heading,
  Text,
  Textfield,
  useTheme,
  LoadingSpinner,
} from '@simplywallst/ui-core';
import { validateDisplayName } from '@utilities/validation';

import { StyledTickSolid } from './styled';

interface Props {
  setHasError: (hasError: boolean) => void;
  username: string;
  setUsername: (username: string) => void;
  mutationStatus: 'idle' | 'pending' | 'success' | 'error';
  mutationErrorMessage?: string;
  resetMutationError: () => void;
}

const Postfix = ({
  mutationStatus,
  error,
}: {
  mutationStatus: MutationStatus;
  error: boolean;
}) => {
  if (mutationStatus === 'pending') {
    return <LoadingSpinner light top="20px" right="8px" />;
  }

  if (mutationStatus === 'success' && !error) {
    return <StyledTickSolid />;
  }
};

const CreateProfileStep = ({
  setHasError,
  username,
  setUsername,
  mutationStatus,
  mutationErrorMessage,
  resetMutationError,
}: Props) => {
  const [error, setError] = useState<string | undefined>();

  const theme = useTheme();
  const handleTextChange = (event: FormEvent<HTMLInputElement>) => {
    resetMutationError();
    setUsername(event.currentTarget.value);
    setHasError(false);
    setError(undefined);
    try {
      validateDisplayName(event.currentTarget.value);
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e.message);
        setHasError(true);
      }
    }
  };

  const handleBlur = (event: FormEvent<HTMLInputElement>) => {
    setHasError(false);
    try {
      setUsername(event.currentTarget.value);
      validateDisplayName(event.currentTarget.value);
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e.message);
        setHasError(true);
      }
    }
  };

  return (
    <Grid gap={`${theme.x3}px`}>
      <Heading as={'h1'} color="brand01" typography="xLargeStrong">
        Create your profile
      </Heading>
      <Text typography="largeStrong">
        Narratives are public. How should Simply Wall St community know you?
      </Text>
      <Textfield
        styleType="standard"
        type="text"
        name="username"
        value={username}
        onChange={handleTextChange}
        onBlur={handleBlur}
        disabled={false}
        error={mutationErrorMessage || error}
        placeholder="Username"
        label="Username"
        hiddenLabel
        autoFocus
        postfix={<Postfix mutationStatus={mutationStatus} error={!!error} />}
      />
    </Grid>
  );
};

export default CreateProfileStep;
